import { cloneDeep, set } from 'lodash';
import { MutationTree } from 'vuex';
import { OrderSubscriptionTableItemKey } from '~/components/order/subscription';

export const state = () => ({
  id: null,
  query: {
    where: {
      purchaseType: [
        'PREORDER',
        'STOCK',
        'UNLOCK_KIT',
        'UNLOCK_REPORT',
        'UNLOCK_PROTOCOL'
      ]
    },
    sort: {
      createdAt: 'DESC'
    },
    offset: 0,
    limit: 10
  },
  table: {
    headers: [],
    footer: {
      'items-per-page-options': [10, 25, 50, 100],
      'show-current-page': true,
      'show-first-last-page': true,
      'page-text': '{{pageStart}}',
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: true
      }
    },
    filter: {
      code: '',
      date: [],
      buyer: '',
      qty: '',
      type: [],
      status: [],
      price: [],
      purchaseType: [
        'PREORDER',
        'STOCK',
        'UNLOCK_KIT',
        'UNLOCK_REPORT',
        'UNLOCK_PROTOCOL'
      ],
      shipmentAddressFormStatus: [],
      isInternationalOrder: undefined,
      merchantType: [],
      merchantId: null,
      orderPaymentId: ''
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['createdAt'],
      sortDesc: [true],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true
    }
  },
  subscription: {
    query: {
      where: {
        purchaseType: ['SUBSCRIPTION'],
        keyword: undefined as undefined | string,
        status: undefined as undefined | string[],
        productIds: undefined as undefined | string[],
        orderDate: undefined as undefined | string[],
        paidAt: undefined as undefined | string[],
        orderSubscription: {
          source: undefined as undefined | string[],
          includeKit: undefined as undefined | boolean,
          subscriptionStartDate: undefined as undefined | string,
          subscriptionEndDate: undefined as undefined | string
        }
      },
      limit: 30,
      include: ['order-subscription'],
      offset: 0,
      sort: {
        createdAt: 'DESC'
      }
    },
    table: {
      filter: {
        keyword: undefined,
        paymentStatus: [],
        productIds: [],
        source: [],
        kitDelivery: 'ALL' as undefined | string,
        orderDate: [],
        paymentDate: [],
        subsStartDate: undefined as undefined | Array<string>,
        subsEndDate: undefined as undefined | Array<string>
      },
      options: {
        page: 1,
        itemsPerPage: 30,
        sortBy: ['createdAt'],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: true
      },
      footer: {
        'items-per-page-options': [30, 50, 100],
        'show-current-page': true,
        'show-first-last-page': true,
        options: {
          page: 1,
          itemsPerPage: 30,
          sortBy: ['createdAt'],
          sortDesc: [true],
          groupBy: [],
          groupDesc: [],
          multiSort: false,
          mustSort: true
        }
      }
    }
  },
  visibleTableHeaders: null
});

export type OrderState = ReturnType<typeof state>;

export const mutations: MutationTree<OrderState> = {
  changeId: (state, id) => (state.id = id),
  changeQuery: (state, query) => Object.assign(state.query, query),
  changeTableFilter: (state, filter) => {
    Object.assign(state.table, { filter });
    Object.assign(state.query, {
      where: {
        ...state.query.where,
        code: filter.code,
        status: filter.status,
        date: filter.date,
        qty: filter.qty,
        price: filter.price,
        buyer: filter.buyer,
        type: filter.type,
        purchaseType:
          Array.isArray(filter.purchaseType) && filter.purchaseType.length > 0
            ? [...filter.purchaseType]
            : [
                'PREORDER',
                'STOCK',
                'UNLOCK_KIT',
                'UNLOCK_REPORT',
                'UNLOCK_PROTOCOL'
              ],
        shipmentAddressFormStatus: filter.shipmentAddressFormStatus,
        isInternationalOrder: filter.isInternationalOrder,
        merchantType: filter.merchantType,
        merchantId: filter.merchantId
      }
    });
  },
  changeTableOptions: (state, options) => {
    Object.assign(state.table, { options });
    Object.assign(state.table.footer, { options });
    Object.assign(state.query, {
      sort: set({}, options.sortBy[0], options.sortDesc[0] ? 'DESC' : 'ASC'),
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage
    });
  },
  changeSubscriptionTableFilter: (state, filter) => {
    Object.assign(state.subscription.table, { filter: cloneDeep(filter) });

    Object.assign(state.subscription.query, {
      where: {
        ...state.subscription.query.where,
        keyword: filter.keyword,
        productIds:
          Array.isArray(filter.productIds) && filter.productIds.length > 0
            ? [...filter.productIds]
            : undefined,
        status:
          Array.isArray(filter.paymentStatus) && filter.paymentStatus.length > 0
            ? [...filter.paymentStatus]
            : undefined,
        date:
          Array.isArray(filter.orderDate) && filter.orderDate.length > 0
            ? [...filter.orderDate]
            : undefined,
        paidAt:
          Array.isArray(filter.paymentDate) && filter.paymentDate.length > 0
            ? [...filter.paymentDate]
            : undefined,
        orderSubscription: {
          source:
            Array.isArray(filter.source) && filter.source.length > 0
              ? [...filter.source]
              : undefined,
          includeKit:
            filter.kitDelivery === 'YES'
              ? true
              : filter.kitDelivery === 'NO'
                ? false
                : undefined,
          subscriptionStartDate:
            Array.isArray(filter.subsStartDate) &&
            filter.subsStartDate.length > 0
              ? [...filter.subsStartDate]
              : undefined,
          subscriptionEndDate:
            Array.isArray(filter.subsEndDate) && filter.subsEndDate.length > 0
              ? [...filter.subsEndDate]
              : undefined
        }
      }
    });
  },
  changeSubscriptionTableOptions: (state, options) => {
    Object.assign(state.subscription.table, { options });
    Object.assign(state.subscription.table.footer, { options });

    // Map sort keys from component to query field names
    let sortKey = options.sortBy[0];
    if (sortKey === OrderSubscriptionTableItemKey.SubsStartDate) {
      sortKey = 'subscriptionStartDate';
    } else if (sortKey === OrderSubscriptionTableItemKey.SubsEndDate) {
      sortKey = 'subscriptionEndDate';
    }

    Object.assign(state.subscription.query, {
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage,
      sort: set({}, sortKey, options.sortDesc[0] ? 'DESC' : 'ASC')
    });
  },
  changeVisibleTableHeaders: (state, values) => {
    state.visibleTableHeaders = values;
  }
};
